import { useCallback } from 'react';

import { loginApi } from 'apis/login';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { authResolverErrorFallThroughValue } from '../../useAuthDataResolver.constants';

import {
  useAuthMagicLink,
  UseAuthMagicLinkResult,
} from './utils/useAuthMagicLink/useAuthMagicLink';
import {
  useAuthOktaClient,
  UseAuthOktaClientResult,
} from './utils/useAuthOktaClient/useAuthOktaClient';

/** Represents the result of the trigger. */
export type UseAuthMethodTriggerResult =
  | UseAuthMagicLinkResult
  | UseAuthOktaClientResult;

/** Represents the trigger for getting the auth data. */
type Trigger = (
  prevError: Error | unknown,
) => Promise<UseAuthMethodTriggerResult>;

/**
 * A hook that resolves the auth data from the dixa login page or the okta
 * client depending on what the auth method endpoint returns from the login
 * api.
 *
 * While we can check for presence of the magic link search params, we have to
 * explicitly query the auth method endpoint for the login method to rule out
 * that there is no okta login method present as well. The backend thus decides
 * the type of login regardless of potentially present search params.
 */
export const useAuthMethod = (): Trigger => {
  const dispatch = useAppDispatch();
  const getAuthDataFromMagicLink = useAuthMagicLink();
  const getAuthDataFromOktaClient = useAuthOktaClient();

  /** Gets the auth data through the magic link or okta client. */
  const trigger = useCallback<Trigger>(
    async (prevError: Error | unknown) => {
      if (
        (typeof prevError === 'string' || prevError instanceof Error) &&
        prevError !== authResolverErrorFallThroughValue
      ) {
        return Promise.reject(prevError);
      }
      const { data } = await dispatch(
        loginApi.endpoints.readAuthMethod.initiate(),
      );

      if (data?.data.auth_method.type === 'dixa_magic_link') {
        return getAuthDataFromMagicLink();
      }

      if (data?.data.auth_method.type === 'okta_openid_connect') {
        return getAuthDataFromOktaClient(data.data.auth_method);
      }

      return Promise.reject(new Error('noAuthMethod'));
    },
    [dispatch, getAuthDataFromMagicLink, getAuthDataFromOktaClient],
  );

  return trigger;
};
